import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Phone,
  Mail,
  MapPin,
  Globe,
  Edit,
  Package,
  Plus,
  RefreshCw,
  Grid,
  List,
  CheckCircle,
  AlertTriangle,
  FileText,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Scale,
  DollarSign,
  Trash2,
  TrendingUp,
  ShoppingCart,
  Weight,
  Users,
  Gift
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getCompanyById, deleteCompany } from '../../api/wholesale/companies';
import {
  getCompanyProducts,
  getCompanyProductsGrouped,
  batchDeleteProducts
} from '../../api/wholesale/companyProducts';
import { getSizeCategories } from '../../api/wholesale/sizeCategories';
import { getTileTypes } from '../../api/wholesale/tileTypes';
import AddCompanyProductModal from '../../components/wholesale/AddCompanyProductModal';
import EditCompanyModal from '../../components/wholesale/EditCompanyModal';
import EditProductModal from '../../components/wholesale/EditProductModal';
import CompanySizeWeights from '../../components/wholesale/CompanySizeWeights';
import CompanyPricing from '../../components/wholesale/CompanyPricing';
import RebateFormModal from '../../components/wholesale/RebateFormModal';
import ManufacturingCompanyStatement from '../../components/wholesale/ManufacturingCompanyStatement';
import { getCompanyStats } from '../../api/wholesale/analytics';
import { getRebates } from '../../api/wholesale/rebates';
import PeriodFilter from '../../components/common/PeriodFilter';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CompanyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getDateRangeParams } = useFiscalPeriod();
  const [localStatsPeriodRange, setLocalStatsPeriodRange] = useState(null);

  // Company state
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Products state
  const [products, setProducts] = useState([]);
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'all'

  // Categories state (for product modal)
  const [sizeCategories, setSizeCategories] = useState([]);
  const [tileTypes, setTileTypes] = useState([]);

  // Modal state
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isEditCompanyModalOpen, setIsEditCompanyModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
  const [isRebateModalOpen, setIsRebateModalOpen] = useState(false);

  // Grouped view state
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const MAX_VISIBLE_PRODUCTS = 48; // Max products before scroll
  const [companyStats, setCompanyStats] = useState(null);

  // Rebates state
  const [rebates, setRebates] = useState([]);
  const [rebatesLoading, setRebatesLoading] = useState(false);

  // Fetch company data
  const fetchCompany = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCompanyById(id);
      setCompany(data?.data || data);

      // Company products and rebates will be fetched in a separate effect
    } catch (err) {
      console.error('Error fetching company:', err);
      setError(err.message || 'Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      // Always fetch flat products list (needed for createdProductAttributes in CompanyPricing)
      const flatData = await getCompanyProducts(id);
      setProducts(Array.isArray(flatData) ? flatData : []);

      // Also fetch grouped products if in grouped view
      if (viewMode === 'grouped') {
        const groupedData = await getCompanyProductsGrouped(id);
        setGroupedProducts(Array.isArray(groupedData) ? groupedData : []);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      toast.error('Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  };

  // Fetch rebates for this company
  const fetchRebates = async () => {
    try {
      setRebatesLoading(true);
      const response = await getRebates({ company_id: id });
      setRebates(response.data || []);
    } catch (err) {
      console.error('Error fetching rebates:', err);
    } finally {
      setRebatesLoading(false);
    }
  };

  // Fetch categories for product creation
  const fetchCategories = async () => {
    try {
      const [sizesData, typesData] = await Promise.all([
        getSizeCategories(),
        getTileTypes({ include_global: true })
      ]);
      setSizeCategories(Array.isArray(sizesData) ? sizesData : []);
      setTileTypes(Array.isArray(typesData) ? typesData : []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    fetchCompany();
    fetchCategories();
  }, [id]);

  useEffect(() => {
    if (company) {
      fetchProducts();
      fetchRebates();
    }
  }, [company, viewMode]);

  // Handle stats refetch on period change
  useEffect(() => {
    const fetchStats = async () => {
      try {
        let statsParams = {};
        if (localStatsPeriodRange && !localStatsPeriodRange.isGlobal) {
          statsParams = {
            start_date: localStatsPeriodRange.start_date,
            end_date: localStatsPeriodRange.end_date
          };

          // Determine if user explicitly chose "All Time" (local override with no dates)
          const isExplicitAllTime =
            !localStatsPeriodRange.start_date && !localStatsPeriodRange.end_date;
          if (isExplicitAllTime) {
            statsParams.ignore_global_period = true;
          }
        } else {
          statsParams = getDateRangeParams();
        }

        const statsRes = await getCompanyStats(id, statsParams);
        setCompanyStats(statsRes?.data || null);
      } catch (err) {
        console.warn('Could not fetch company stats on period update:', err);
      }
    };

    if (company) {
      fetchStats();
    }
  }, [id, localStatsPeriodRange, company]);

  const handleProductAdded = () => {
    fetchProducts();
    toast.success('Product(s) added successfully');
  };

  const handleCompanyUpdated = () => {
    fetchCompany();
    toast.success('Company updated successfully');
  };

  const handleDeleteCompany = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${company.company_name}"?\n\nThis will move the company and all its products to the recycle bin.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await deleteCompany(id);
      toast.success('Company deleted successfully');
      navigate('/wholesale/companies');
    } catch (err) {
      console.error('Error deleting company:', err);
      toast.error(err.message || 'Failed to delete company');
      setLoading(false);
    }
  };

  // Handle product click - open edit modal
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsEditProductModalOpen(true);
  };

  const handleProductUpdated = () => {
    fetchProducts();
  };

  const handleProductDeleted = () => {
    fetchProducts();
  };

  const handleDeleteTileTypeProducts = async (tileTypeName, products) => {
    const productCount = products.length;
    if (
      !window.confirm(
        `Are you sure you want to delete all ${productCount} product(s) in "${tileTypeName}"?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const productIds = products.map((p) => p.id);
      await batchDeleteProducts(productIds);
      toast.success(`${productCount} product(s) deleted successfully`);
      fetchProducts();
    } catch (err) {
      console.error('Error deleting products:', err);
      toast.error(err.message || 'Failed to delete products');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !company) {
    return (
      <div className="space-y-6">
        <Link
          to="/wholesale/companies"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Companies
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Company not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/wholesale/companies"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{company.company_name}</h1>
            <p className="text-gray-500">{company.company_code}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsEditCompanyModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
          >
            <Edit className="w-4 h-4" />
            Edit Company
          </button>
        </div>
      </div>

      {/* Products Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Products</h2>
              <p className="text-sm text-gray-500">
                {viewMode === 'grouped'
                  ? `${groupedProducts.length} size categories`
                  : `${products.length} products`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex items-center gap-0.5 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setViewMode('grouped')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grouped' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
                title="View by Category"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'all' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                }`}
                title="View All"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={fetchProducts}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-4 h-4 ${productsLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsAddProductModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          </div>
        </div>

        {productsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
          </div>
        ) : viewMode === 'grouped' ? (
          // Grouped View - One size at a time with tabs
          groupedProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No products yet</h3>
              <p className="text-gray-500 mb-4">Add products to this company</p>
              <button
                onClick={() => setIsAddProductModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4" />
                Add Product
              </button>
            </div>
          ) : (
            <div>
              {/* Size Category Tabs */}
              <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
                {groupedProducts.map((sizeGroup, index) => {
                  const productCount =
                    sizeGroup.tile_types?.reduce((acc, t) => acc + (t.products?.length || 0), 0) ||
                    0;
                  return (
                    <button
                      key={sizeGroup.size_category_id}
                      onClick={() => setSelectedSizeIndex(index)}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium transition-all ${
                        selectedSizeIndex === index
                          ? 'bg-emerald-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {sizeGroup.size_category_name}
                      <span
                        className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                          selectedSizeIndex === index ? 'bg-white/20' : 'bg-gray-200'
                        }`}
                      >
                        {productCount}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Sort Controls */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-500">
                  {(() => {
                    const current = groupedProducts[selectedSizeIndex];
                    const count =
                      current?.tile_types?.reduce((acc, t) => acc + (t.products?.length || 0), 0) ||
                      0;
                    return `${count} product${count !== 1 ? 's' : ''}`;
                  })()}
                </p>
                <button
                  onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {sortOrder === 'asc' ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  Sort {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
                </button>
              </div>

              {/* Products Display Area */}
              {groupedProducts[selectedSizeIndex] && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-[400px] overflow-y-auto p-4 space-y-4">
                    {groupedProducts[selectedSizeIndex].tile_types?.map((typeGroup) => {
                      // Sort products by tile_code
                      const sortedProducts = [...(typeGroup.products || [])].sort((a, b) => {
                        const codeA = (a.tile_code || '').toUpperCase();
                        const codeB = (b.tile_code || '').toUpperCase();
                        return sortOrder === 'asc'
                          ? codeA.localeCompare(codeB)
                          : codeB.localeCompare(codeA);
                      });

                      return (
                        <div key={typeGroup.tile_type_id || 'uncategorized'}>
                          <div className="flex items-center justify-between sticky top-0 bg-white py-1 mb-2">
                            <h4 className="text-sm font-medium text-gray-700">
                              {typeGroup.tile_type_name}
                              <span className="ml-2 text-xs text-gray-400">
                                ({sortedProducts.length})
                              </span>
                            </h4>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTileTypeProducts(
                                  typeGroup.tile_type_name,
                                  sortedProducts
                                );
                              }}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title={`Delete all ${sortedProducts.length} products in ${typeGroup.tile_type_name}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {sortedProducts.map((product) => (
                              <div
                                key={product.id}
                                onClick={() => handleProductClick(product)}
                                className="px-3 py-2 bg-gray-50 rounded-lg hover:bg-emerald-50 hover:border-emerald-300 border border-gray-200 cursor-pointer transition-colors"
                              >
                                <p className="text-sm font-mono font-medium text-gray-900">
                                  {product.tile_code}
                                </p>
                                {product.name && (
                                  <p className="text-xs text-gray-500 truncate max-w-[120px]">
                                    {product.name}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        ) : // All Products View
        products.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products yet</h3>
            <p className="text-gray-500 mb-4">Add products to this company</p>
            <button
              onClick={() => setIsAddProductModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {products.map((product) => (
              <div
                key={product.id}
                onClick={() => handleProductClick(product)}
                className="px-3 py-2 bg-gray-50 rounded-lg hover:bg-emerald-50 hover:border-emerald-300 border border-gray-200 cursor-pointer transition-colors"
              >
                <p className="text-sm font-mono font-medium text-gray-900">{product.tile_code}</p>
                <p className="text-xs text-gray-500">{product.size_category_name}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <Scale className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Size Weights</h2>
            <p className="text-sm text-gray-500">
              Configure weight per carton for created product sizes
            </p>
          </div>
        </div>
        <CompanySizeWeights
          companyId={parseInt(id)}
          activeSizeCategoryId={
            viewMode === 'grouped' && groupedProducts[selectedSizeIndex]
              ? groupedProducts[selectedSizeIndex].size_category_id
              : null
          }
          availableSizeCategoryIds={
            viewMode === 'grouped'
              ? groupedProducts.map((g) => g.size_category_id)
              : [...new Set(products.map((p) => p.size_category_id).filter(Boolean))]
          }
        />
      </div>

      {/* Selling Prices Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Selling Prices</h2>
            <p className="text-sm text-gray-500">Configure price zones and code pattern pricing</p>
          </div>
        </div>
        <CompanyPricing
          companyId={parseInt(id)}
          createdProductAttributes={products.map((p) => ({
            size_category_id: p.size_category_id,
            tile_type_id: p.tile_type_id
          }))}
        />
      </div>

      {/* Rebates/Incentives Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Gift className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Rebates & Incentives</h2>
              <p className="text-sm text-gray-500">Monthly and yearly rebates from this company</p>
            </div>
          </div>
          <button
            onClick={() => setIsRebateModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Rebate
          </button>
        </div>

        {rebatesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-4 border-emerald-500 border-t-transparent"></div>
          </div>
        ) : rebates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Gift className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>No rebates recorded yet</p>
            <button
              onClick={() => setIsRebateModalOpen(true)}
              className="text-emerald-600 hover:underline text-sm mt-1"
            >
              Add first rebate
            </button>
          </div>
        ) : (
          <div>
            {/* Summary */}
            <div className="flex gap-4 mb-4">
              <div className="flex-1 bg-emerald-50 rounded-lg p-3">
                <p className="text-xs text-emerald-700 font-medium">Total Rebates</p>
                <p className="text-xl font-bold text-emerald-600">
                  ₦{rebates.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="flex-1 bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-700 font-medium">Monthly</p>
                <p className="text-xl font-bold text-blue-600">
                  {rebates.filter((r) => r.rebate_type === 'monthly').length}
                </p>
              </div>
              <div className="flex-1 bg-purple-50 rounded-lg p-3">
                <p className="text-xs text-purple-700 font-medium">Yearly</p>
                <p className="text-xl font-bold text-purple-600">
                  {rebates.filter((r) => r.rebate_type === 'yearly').length}
                </p>
              </div>
            </div>

            {/* Recent rebates list */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                      Period
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rebates.slice(0, 5).map((rebate) => (
                    <tr key={rebate.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-700">
                        {rebate.rebate_type === 'monthly'
                          ? `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][rebate.rebate_month - 1]} ${rebate.rebate_year}`
                          : `Year ${rebate.rebate_year}`}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            rebate.rebate_type === 'monthly'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {rebate.rebate_type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-emerald-600">
                        ₦{parseFloat(rebate.amount).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {rebates.length > 5 && (
              <Link
                to={`/wholesale/rebates?company_id=${id}`}
                className="block text-center text-emerald-600 hover:underline text-sm mt-2"
              >
                View all {rebates.length} rebates →
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Loading Statistics - After Selling Prices */}
      {companyStats && (
        <div className="bg-theme-secondary rounded-xl border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-theme-primary">Loading Statistics</h2>
                <p className="text-sm text-theme-secondary">
                  Overview of cartons, tonnage, and value loaded
                </p>
              </div>
            </div>
            <PeriodFilter
              onChange={setLocalStatsPeriodRange}
              storageKey="mfg_stats_period"
              compact={true}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800/30">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Total Cartons
                </span>
              </div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-200">
                {parseInt(companyStats.total_cartons || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-100 dark:border-orange-800/30">
              <div className="flex items-center gap-2 mb-2">
                <Weight className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
                  Total Tonnage
                </span>
              </div>
              <p className="text-2xl font-bold text-orange-700 dark:text-orange-200">
                {parseFloat(companyStats.total_tonnage || 0).toFixed(2)} T
              </p>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-100 dark:border-emerald-800/30">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                  Total Value
                </span>
              </div>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-200">
                ₦{(parseFloat(companyStats.total_value || 0) / 1000000).toFixed(2)}M
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-100 dark:border-purple-800/30">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingCart className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <span className="text-sm font-medium text-purple-800 dark:text-purple-300">
                  Loaded Orders
                </span>
              </div>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-200">
                {companyStats.total_orders || 0}
              </p>
            </div>
          </div>

          {companyStats.size_breakdown && Object.keys(companyStats.size_breakdown).length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <p className="text-sm font-medium text-theme-secondary mb-2">Cartons by Size:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(companyStats.size_breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([size, count]) => (
                    <span
                      key={size}
                      className="px-3 py-1 bg-gray-50 rounded-full text-sm border border-gray-200"
                    >
                      <span className="font-medium text-theme-primary">{size}:</span>{' '}
                      <span className="text-theme-secondary">{count.toLocaleString()}</span>
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Customer Rankings */}
          {companyStats.customer_rankings && companyStats.customer_rankings.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-theme-primary mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-600" />
                Top Customers
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-theme-secondary">
                      <th className="pb-2 font-medium">#</th>
                      <th className="pb-2 font-medium">Customer</th>
                      <th className="pb-2 font-medium text-right">Orders</th>
                      <th className="pb-2 font-medium text-right">Cartons</th>
                      <th className="pb-2 font-medium text-right">Tonnage</th>
                      <th className="pb-2 font-medium text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyStats.customer_rankings.map((customer) => (
                      <tr key={customer.distributor_id} className="border-t border-gray-100">
                        <td className="py-2 font-bold text-blue-600">{customer.rank}</td>
                        <td className="py-2">
                          <Link
                            to={`/wholesale/distributors/${customer.distributor_id}`}
                            className="font-medium text-theme-primary hover:text-blue-600 hover:underline"
                          >
                            {customer.business_name}
                          </Link>
                          {customer.contact_person_name && (
                            <p className="text-xs text-theme-secondary">
                              {customer.contact_person_name}
                            </p>
                          )}
                        </td>
                        <td className="py-2 text-right text-theme-secondary">
                          {customer.total_orders}
                        </td>
                        <td className="py-2 text-right text-theme-secondary">
                          {parseInt(customer.total_cartons).toLocaleString()}
                        </td>
                        <td className="py-2 text-right text-theme-secondary">
                          {parseFloat(customer.total_tonnage).toFixed(2)}T
                        </td>
                        <td className="py-2 text-right font-medium text-emerald-600">
                          ₦{(parseFloat(customer.total_value) / 1000000).toFixed(2)}M
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Company Info Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Company Information</h2>
            <div className="flex items-center gap-2 mt-1">
              {company.is_ftz && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  <CheckCircle className="w-3 h-3" />
                  FTZ
                </span>
              )}
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  company.company_status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {company.company_status || 'Active'}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Contact Info */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">Contact Information</h3>
            {company.primary_phone && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4" />
                <span>{company.primary_phone}</span>
              </div>
            )}
            {company.whatsapp_number && company.whatsapp_number !== company.primary_phone && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4 text-green-600" />
                <span>WhatsApp: {company.whatsapp_number}</span>
              </div>
            )}
            {company.email && (
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4" />
                <span>{company.email}</span>
              </div>
            )}
            {company.website_url && (
              <div className="flex items-center gap-2 text-gray-600">
                <Globe className="w-4 h-4" />
                <a
                  href={company.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-600 hover:underline"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>

          {/* Address */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">Address</h3>
            {(company.factory_address || company.address) && (
              <div className="flex items-start gap-2 text-gray-600">
                <MapPin className="w-4 h-4 mt-0.5" />
                <div>
                  <p>{company.factory_address || company.address}</p>
                  {company.city && (
                    <p>
                      {company.city}
                      {company.state ? `, ${company.state}` : ''}
                    </p>
                  )}
                  {company.country && <p>{company.country}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Contact Person */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">Contact Person</h3>
            {company.primary_contact_person && (
              <div className="text-gray-600">
                <p className="font-medium">{company.primary_contact_person}</p>
                {company.contact_person_phone && <p>{company.contact_person_phone}</p>}
                {company.contact_person_email && <p>{company.contact_person_email}</p>}
              </div>
            )}
            {company.contact_person_1_name && (
              <div className="text-gray-600">
                <p className="font-medium">{company.contact_person_1_name}</p>
                {company.contact_person_1_phone && <p>{company.contact_person_1_phone}</p>}
                {company.contact_person_1_email && <p>{company.contact_person_1_email}</p>}
              </div>
            )}
          </div>
        </div>

        {company.notes && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
            <p className="text-gray-600">{company.notes}</p>
          </div>
        )}
      </div>

      {/* Statement of Account Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Statement of Account</h2>
            <p className="text-sm text-gray-500">
              View financial statement mapping loadings, payments, expenses, and rebates
            </p>
          </div>
        </div>
        <ManufacturingCompanyStatement
          companyId={parseInt(id)}
          companyName={company.company_name}
        />
      </div>

      {/* Danger Zone Section */}
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-red-700">Danger Zone</h2>
            <p className="text-sm text-red-600">Irreversible and destructive actions</p>
          </div>
        </div>
        <div className="border-t border-red-100 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900">Delete this company</h3>
              <p className="text-sm text-gray-500">
                Once deleted, the company and all its products will be moved to the recycle bin.
              </p>
            </div>
            <button
              onClick={handleDeleteCompany}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete Company
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddCompanyProductModal
        isOpen={isAddProductModalOpen}
        onClose={() => setIsAddProductModalOpen(false)}
        companyId={parseInt(id)}
        sizeCategories={sizeCategories}
        tileTypes={tileTypes}
        onSuccess={handleProductAdded}
      />

      {isEditCompanyModalOpen && (
        <EditCompanyModal
          isOpen={isEditCompanyModalOpen}
          onClose={() => setIsEditCompanyModalOpen(false)}
          company={company}
          onSuccess={handleCompanyUpdated}
        />
      )}

      <EditProductModal
        isOpen={isEditProductModalOpen}
        onClose={() => {
          setIsEditProductModalOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onProductUpdated={handleProductUpdated}
        onProductDeleted={handleProductDeleted}
      />

      <RebateFormModal
        isOpen={isRebateModalOpen}
        onClose={() => setIsRebateModalOpen(false)}
        companyId={parseInt(id)}
        onSuccess={fetchRebates}
      />
    </div>
  );
};

export default CompanyDetailPage;
